import { useCosmos } from 'providers/contexts/CosmosContext';
import { createContext, memo, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NotifierLogsModal } from './NotifierLogsModal';
import { NotifierMessageModal } from './NotifierMessageModal';

/**
 * Modals context wrapper for Notifier Search
 *
 * Mounts:
 * Edit/Create Modal for Notifier Message
 * Per-target Delivery Logs Modal (CMSADMIN-2626)
 *
 * Query params "openModal" for edit/create mode
 * - if openModal is set to a message ID, opens edit modal
 * - if openModal is set to "create", opens create modal
 *
 * Query param "viewLogs" for the delivery logs modal
 * - if viewLogs is set to a message ID, opens the logs modal for that send
 *
 */

type ModalModes = 'create' | 'edit' | null;

export type NotifierContextType = {
  // modal / qp state
  msgModalId: string | null;
  msgModalOpen: boolean;
  msgModalMode: ModalModes;
  handleCloseMsgModal: () => void;
  handleOpenCreateMsgModal: () => void;
  // delivery logs modal
  logsModalId: string | null;
  logsModalOpen: boolean;
  handleOpenLogsModal: (messageId: string) => void;
  handleCloseLogsModal: () => void;
};

const NotifierModalContext = createContext<NotifierContextType | undefined>(undefined);

export const useNotifierModalContext = () => {
  const context = useContext(NotifierModalContext);
  if (!context) {
    throw new Error(
      'useNotifierModalContext must be used within a NotifierModalContextProvider',
    );
  }
  return context;
};

type NotifierModalContextProviderProps = {
  children: React.ReactNode;
};

export const NotifierModalContextProvider = memo(
  ({ children }: NotifierModalContextProviderProps) => {
    const [searchParams, setSearchParams] = useSearchParams();

    /**
     * Modal state from query params
     * Edit modal fetch id:  /broadcast/message/{id}/
     * - Create modal: openModal="create"
     * - Edit modal: openModal={msgId}
     * - Null/closed: no openModal qp
     */
    const msgModalId = searchParams.get('openModal') ?? null;
    const msgModalOpen = Boolean(msgModalId); // shows modal if qp exists
    const msgModalMode: ModalModes =
      msgModalId === 'create' ? 'create' : msgModalOpen ? 'edit' : null;

    /**
     * Remove the openModal qp to close the modal
     */
    const handleCloseMsgModal = useCallback((): void => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openModal');
      setSearchParams(newSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    /**
     * Set the openModal qp in "create" mode
     */
    const handleOpenCreateMsgModal = useCallback((): void => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('openModal', 'create');
      setSearchParams(newSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    /**
     * Delivery logs modal state from the "viewLogs" query param.
     * The logs API is staff-only, so ignore the query param for
     * non-staff users (a deep link would just 403 inside the modal).
     */
    const { userInfo } = useCosmos();
    const isStaffUser = userInfo?.account_type === 'staff';
    const logsModalId = searchParams.get('viewLogs') ?? null;
    const logsModalOpen = Boolean(logsModalId) && isStaffUser;

    const handleOpenLogsModal = useCallback(
      (messageId: string): void => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('viewLogs', messageId);
        setSearchParams(newSearchParams, { replace: false });
      },
      [searchParams, setSearchParams],
    );

    const handleCloseLogsModal = useCallback((): void => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('viewLogs');
      setSearchParams(newSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    /**
     * Context value
     */
    const contextValue = useMemo(() => {
      return {
        msgModalId: msgModalId,
        msgModalOpen: msgModalOpen,
        msgModalMode: msgModalMode,
        handleCloseMsgModal: handleCloseMsgModal,
        handleOpenCreateMsgModal: handleOpenCreateMsgModal,
        logsModalId: logsModalId,
        logsModalOpen: logsModalOpen,
        handleOpenLogsModal: handleOpenLogsModal,
        handleCloseLogsModal: handleCloseLogsModal,
      };
    }, [
      msgModalId,
      msgModalOpen,
      msgModalMode,
      handleCloseMsgModal,
      handleOpenCreateMsgModal,
      logsModalId,
      logsModalOpen,
      handleOpenLogsModal,
      handleCloseLogsModal,
    ]);

    return (
      <NotifierModalContext.Provider value={contextValue}>
        {msgModalOpen && (
          <NotifierMessageModal
            msgModalId={msgModalId}
            msgModalOpen={msgModalOpen}
            msgModalMode={msgModalMode}
            handleCloseMsgModal={handleCloseMsgModal}
          />
        )}
        {logsModalOpen && (
          <NotifierLogsModal
            open={logsModalOpen}
            onClose={handleCloseLogsModal}
            messageId={logsModalId}
          />
        )}
        {children}
      </NotifierModalContext.Provider>
    );
  },
);
NotifierModalContextProvider.displayName = 'NotifierModalContextProvider';
